/** 地图只读重新定位：工程地图、公司地图和项目详情共用，修复旧坐标而不改写业务库。
 *
 * 【2026-10-24 需求·打开地图慢 + Google API 消耗快】旧版对每个地址单独发一条
 * GET /maps/resolve-address（一次打开几十上百条请求），结果只在模块内存里、
 * 刷新页面就全部重查。现改为三层缓存 + 批量接口，把「每次打开地图」的请求量
 * 从 N 条压到 0~2 条（命中缓存时为 0）：
 *   L1 sessionStorage（当前标签页 24h，刷新 / 回退浏览器直接复用）
 *   L2 模块内存缓存（SPA 内导航共享；verified / 约略 24h / 失败 30s）
 *   L3 后端 map_geocodes 持久缓存（verified 30 天 / 约略 7 天，发版不清空）
 * 请求侧改为 POST /maps/resolve-addresses 只批量取缺失地址（每批 ≤8、并发 ≤2 批），
 * 未命中的地址由后端复用既有的并发合并与限流，冷缓存表现与旧版一致。
 * 「重新定位」按钮（全员可用）走 relocateLocation() → POST /maps/relocate，
 * 绕过三层缓存强制重查一次，结果回灌 L1/L2 并广播重绘（后端按地址 10 秒冷却限频）。
 */
import { useEffect, useMemo, useReducer } from "react";
import { cleanMapAddress, validCoordinates } from "../lib/projectLocation";

const SS_KEY = "aligcus.mapGeo.v1";
const DAY = 24 * 3600 * 1000;
// L2/L1 的 TTL 口径：verified 24h——后端还有 30 天持久层兜底，无需更短；
// 约略结果与精确结果一样保留 24h；后端持久层 7 天作为跨标签页兜底。低精度结果
// 仍可用“重新定位”立即刷新。失败 30s，服务恢复后自动重试。
const ttlOf = (result) => (result?.status === "verified" ? DAY : 30 * 1000);

const cache = new Map();       // key(清洗+小写地址) -> { result?, expires?, promise? }
const listeners = new Set();   // 批量结果到达 / 重新定位完成后通知活跃 hook 重绘
const keyOf = (p) => cleanMapAddress(p?.address).toLowerCase();

// —— L1 sessionStorage：进入页面时恢复上次会话的结果，只存可复用项（失败不存）——
try {
  const saved = JSON.parse(sessionStorage.getItem(SS_KEY) || "null");
  if (saved && saved.v === 1) {
    for (const [key, entry] of Object.entries(saved.entries || {})) {
      if (entry?.expires > Date.now() && entry.result?.status && entry.result.status !== "unresolved") cache.set(key, entry);
    }
  }
} catch { /* sessionStorage 损坏 / 不可用时按无缓存处理 */ }

const persist = () => {
  try {
    const entries = [];
    for (const [key, entry] of cache) {
      if (entry.promise || !entry.result || entry.result.status === "unresolved" || !entry.expires || entry.expires <= Date.now()) continue;
      entries.push([key, { result: entry.result, expires: entry.expires }]);
    }
    // 容量兜底：按最快过期先淘汰，最多 400 条（几 KB 量级，远低于 5MB 配额）。
    // 必须存成对象（恢复端按 Object.entries 读）；存成数组会让刷新后的恢复全部失配。
    entries.sort((a, b) => a[1].expires - b[1].expires);
    sessionStorage.setItem(SS_KEY, JSON.stringify({ v: 1, entries: Object.fromEntries(entries.slice(-400)) }));
  } catch { /* 隐私模式等写入失败不影响功能 */ }
};

const notify = () => { for (const fn of listeners) fn(); };

// —— 批量请求：每批 ≤8 条、全局并发 ≤2 批。冷缓存时图钉分批出现（与旧版逐条
// 出现的节奏相近），命中 L3 时整批毫秒级返回、图钉一次到位。——
const CHUNK = 8;
const MAX_PARALLEL = 2;
let activeChunks = 0;
const chunkQueue = [];

const runChunk = (api, chunk, entries) => api.post("/maps/resolve-addresses", { addresses: chunk }, { timeout: 60000 })
  .then(({ data }) => {
    const results = data?.results || {};
    for (const address of chunk) {
      const entry = entries.get(address);
      if (!entry) continue;
      const result = results[address] || { status: "unresolved" };
      entry.result = result;
      entry.expires = Date.now() + ttlOf(result);
      entry.promise = null;
    }
    persist();
  })
  .catch(() => {
    // 整批失败按 30s 短 TTL 处理：一次网络抖动不能把「待核对」占住 24h
    for (const address of chunk) {
      const entry = entries.get(address);
      if (!entry) continue;
      entry.result = { status: "unresolved" };
      entry.expires = Date.now() + 30 * 1000;
      entry.promise = null;
    }
  });

const pumpChunks = () => {
  while (activeChunks < MAX_PARALLEL && chunkQueue.length) {
    const { api, chunk, entries } = chunkQueue.shift();
    activeChunks += 1;
    runChunk(api, chunk, entries).finally(() => { activeChunks -= 1; notify(); pumpChunks(); });
  }
};

/** 输入已有权限的项目清单，返回保留业务字段、覆盖显示坐标的清单；旧请求不能覆盖新地址。 */
export function useProjectLocations(projects, api, enabled = true) {
  const [version, redraw] = useReducer((v) => v + 1, 0);
  // 统一「清洗 + 小写」后作为缓存键与请求键：大小写不同的同一地址共享一条缓存，
  // 也与后端持久层的键口径一致。
  const addresses = JSON.stringify([...new Set(projects.map((p) => cleanMapAddress(p.address).toLowerCase()).filter(Boolean))]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const onChange = () => { if (!cancelled) redraw(); };
    listeners.add(onChange);
    const wanted = JSON.parse(addresses);
    // 三层都没命中（或已过期）的地址才发起请求；全部命中时 0 请求直接出图
    const missing = wanted.filter((address) => {
      const entry = cache.get(address);
      return !entry || (!entry.promise && (!entry.result || entry.expires <= Date.now()));
    });
    const entries = new Map();
    for (const address of missing) {
      const entry = { promise: true };
      cache.set(address, entry);
      entries.set(address, entry);
    }
    // entries 按地址文本隔离：地址被改后旧批次的迟到响应只写旧键，不会串到新地址
    for (let i = 0; i < missing.length; i += CHUNK) chunkQueue.push({ api, chunk: missing.slice(i, i + CHUNK), entries });
    pumpChunks();
    return () => { cancelled = true; listeners.delete(onChange); };
  }, [api, addresses, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(() => projects.map((p) => {
    const cached = cache.get(keyOf(p));
    // TTL 只决定下次进入/刷新是否重查；不能在没有新请求时把失败状态变成永久“正在定位”。
    const result = cached?.result;
    const verified = enabled && result?.status === "verified";
    return { ...p, lat: verified ? Number(result.lat) : null, lng: verified ? Number(result.lng) : null,
      locationStatus: result?.status || (p.address ? "pending" : "unresolved"),
      locationPrecision: verified ? result.precision || "address" : null,
      locationAddress: verified ? result.formattedAddress : null };
  // 缓存在模块内，同一 projects 引用下也要在请求完成后重算，version 是显式的缓存刷新信号。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [projects, enabled, version]);
}

/** 「重新定位」（2026-10-24，全员可用）：绕过三层缓存强制重查一个地址。
 * 后端 force=True 同步返回最新结果；这里回灌 L1/L2 并广播重绘。
 * 返回结果对象（{status, lat, lng, precision?, ...}）供调用方决定提示文案。 */
export async function relocateLocation(api, address) {
  const cleaned = cleanMapAddress(address);
  const { data } = await api.post("/maps/relocate", { address: cleaned }, { timeout: 60000 });
  const result = data && data.status ? data : { status: "unresolved" };
  cache.set(cleaned.toLowerCase(), { result, expires: Date.now() + ttlOf(result) });
  persist();
  notify();
  return result;
}
